<template>
  <v-form ref="form" lazy-validation @submit.prevent>
    <FormSection v-if="summaryIntro">
      <BaseHeading :level="2">{{ summaryIntro }}</BaseHeading>
    </FormSection>

    <FormSection v-for="(info, index) in summaryInfos" :key="index">
      <BaseHeading v-if="info.heading && info.html" class="text-uppercase" :level="3">
        {{ info.heading }}
      </BaseHeading>

      <BaseAlert>
        <template #message>
          <BaseRichtext v-if="info.html" :html="info.html" />
        </template>
      </BaseAlert>
    </FormSection>

    <TreeViewTable v-if="isComponentTicketType" :selections="tableData" />
    <FormSection>
      <BaseCheckbox
        v-model="acceptTerms"
        :error-messages="acceptTermsErrors"
        required
        @input="touch('acceptTerms')"
        @blur="touch('acceptTerms')"
      >
        <BaseRichtext :html="acceptTermsText" />
      </BaseCheckbox>

      <BaseCheckbox
        v-model="privacy"
        :error-messages="privacyErrors"
        required
        @input="touch('privacy')"
        @blur="touch('privacy')"
        >{{ $t('complaintForm.summaryDataProtectionInfo', { link: privaryUrl }) }}</BaseCheckbox
      >
    </FormSection>

    <BaseAlert
      v-if="!hasRole(ROLES.SERVICE_ENGINE_W.p)"
      class="mt-8"
      type="error"
      :message="$t('checkout.no_permission_to_submit')"
    />

    <ComplaintFormStepperControls
      :is-valid="isValid && hasRole(ROLES.SERVICE_ENGINE_W.p)"
      :has-required-fields="true"
      :prev-step="prevStep"
      :finish-step="submit"
    />
  </v-form>
</template>

<script>
/*
CUSTOMS FORM DISABLED

    <FormSection v-if="customsFormUrl" class="mt-3">
      <BaseCheckbox
        v-model="customsForm"
        class="pt-0 mt-0"
        :error-messages="customsFormErrors"
        required
        @input="touch('customsForm')"
        @blur="touch('customsForm')"
        ><div>
          <h3 class="mb-1">
            {{ $t('complaintForm.ticket.engine.summary_customs_form_headline') }}
          </h3>
          {{ $t('complaintForm.ticket.engine.summary_customs_form_text') }}
        </div>
      </BaseCheckbox>
      <ul class="ml-5">
        <li v-for="(url, lang) in customsFormUrl" :key="lang">
          <a :href="url" download target="_blank">{{
            $t(`complaintForm.ticket.engine.summary_customs_form_title_${lang}`)
          }}</a>
        </li>
      </ul>
    </FormSection>

*/
import { get } from 'lodash-es'
import { mapMutations, mapGetters, mapActions } from 'vuex'

import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'

import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'
import PermissionMixin from '@/js/mixins/permission-mixin.js'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_ACTION from '@/js/components/Forms/ComplaintForm/_store-module/action-types.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'

import APP_GETTER from '@/js/plugins/store/modules/app/getter-types.js'

import FormSection from '@/js/components/Forms/FormSection.vue'

import ComplaintFormStepperControls from '../ComplaintFormStepperControls.vue'
import TreeViewTable from '@/js/components/Forms/TreeView/TreeViewTable'

export default {
  name: 'ComplaintFormSummary',
  components: {
    FormSection,
    ComplaintFormStepperControls,
    TreeViewTable,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin, PermissionMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      acceptTerms: false,
      privacy: false,
      // customsForm: false,
    }
  },
  validations: {
    acceptTerms: {
      checked(val) {
        return val
      },
    },
    privacy: {
      checked(val) {
        return val
      },
    },
    // customsForm: {
    //   checked(val) {
    //     return !this.customsFormUrl || val
    //   },
    // },
  },
  computed: {
    ...mapGetters(STOREMODULES.APP, {
      getLinkByType: APP_GETTER.APP_CONFIG_LINK_BY_TYPE,
    }),
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      steps: FORM_GETTER.STEPS,
      // paymentAddress: FORM_GETTER.PAYMENT_ADDRESS,
      calculationDesire: FORM_GETTER.CALCULATION_DESIRE,
      getTableData: FORM_GETTER.FORM_DATA,
    }),

    tableData() {
      return get(this.getTableData, 'claim', [])
    },
    ticketType() {
      return get(this.getTableData, 'ticketType', '')
    },
    isComponentTicketType() {
      return this.ticketType === FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.TICKET_TYPES.COMPONENT
    },
    formData() {
      return {
        privacy: this.privacy,
        acceptTerms: this.acceptTerms,
      }
    },
    summaryStepData() {
      return get(this.steps, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.STEPS.SUMMARY)
    },
    summaryIntro() {
      return get(this.summaryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.HEADER)
    },
    summaryInfos() {
      return [
        {
          heading: get(this.summaryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.COSTS_HEADER),
          html: get(this.summaryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.COSTS_TEXT),
        },
        {
          heading: get(this.summaryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.SHIPPING_HEADER),
          html: get(this.summaryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.SHIPPING_TEXT),
        },
        {
          heading: get(this.summaryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.WARRANTY_HEADER),
          html: get(this.summaryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.WARRANTY_TEXT),
        },
      ]
    },
    acceptTermsText() {
      return (
        get(this.summaryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.AGB) ||
        this.$t('complaintForm.summaryTermsCheckbox')
      )
    },
    privaryUrl() {
      return this.getLinkByType('privacy')
    },

    /** Errors */
    acceptTermsErrors() {
      return this.formErrorHelper('acceptTerms', [
        {
          condition: 'checked',
          error: this.chooseErrorMessage,
        },
      ])
    },
    privacyErrors() {
      return this.formErrorHelper('privacy', [
        {
          condition: 'checked',
          error: this.chooseErrorMessage,
        },
      ])
    },
    // customsFormErrors() {
    //   return this.formErrorHelper('customsForm', [
    //     {
    //       condition: 'checked',
    //       error: this.chooseErrorMessage,
    //     },
    //   ])
    // },
    isPickupCalculationDesire() {
      return [
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
          .CALCULATION_DESIRE_NEW_ENGINE_WITH_LEGACY_DEVICE_PICK_UP,
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
          .CALCULATION_DESIRE_POOL_ENGINE_WITH_LEGACY_DEVICE_PICK_UP,
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_FEE_BASED,
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_GUARANTEE,
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_POOL_ENGINE_IN_WARRANTY_PERIOD,
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_NEW_ENGINE_IN_WARRANTY_PERIOD,
      ].includes(this.calculationDesire)
    },
    // paymentAddressCountryId() {
    //   return `${get(this.paymentAddress, 'CountryId')}`.toLowerCase()
    // },
    // customsFormUrl() {
    //   if (!(this.isPickupCalculationDesire && (this.isBuyEngineForm || this.isEngineForm))) {
    //     return undefined
    //   }
    //   switch (this.paymentAddressCountryId) {
    //     case 'ch':
    //       return {
    //         de: this.$t('complaintForm.ticket.engine.summary_customs_form_url_de'),
    //         fr: this.$t('complaintForm.ticket.engine.summary_customs_form_url_fr'),
    //       }
    //     // case 'gb':
    //     case 'no':
    //       return {
    //         en: this.$t('complaintForm.ticket.engine.summary_customs_form_url_en'),
    //       }
    //     default:
    //       return undefined
    //   }
    // },
  },

  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
    }),
    ...mapActions(FORM_CONSTANTS.STORE_ID, {
      createTicket: FORM_ACTION.CREATE_TICKET,
    }),
    submitCallback() {
      this.updateFormData(this.formData)
      this.createTicket(this.formData)
    },
  },
}
</script>
